import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [authError, setAuthError] = useState(null);

  // Enhanced profile operations with better error handling and debugging
  const profileOperations = {
    async load(userId) {
      if (!userId) {
        setProfileLoading(false);
        return;
      }
      setProfileLoading(true);
      console.log('DEBUGGING - Loading profile for user:', userId);
      try {
        const { data, error } = await supabase
          ?.from('user_profiles')
          ?.select('*')
          ?.eq('id', userId)
          ?.single();

        console.log('DEBUGGING - Profile query result:', {
          data: data,
          error: error,
          hasData: !!data,
          role: data?.role
        });

        if (!error && data) {
          setUserProfile(data);
          // Store user role in localStorage for UI logic
          localStorage?.setItem('userRole', data?.role || 'client');
          localStorage?.setItem('userName', data?.full_name || 'User');
          console.log('DEBUGGING - Profile loaded successfully:', {
            role: data?.role,
            full_name: data?.full_name,
            email: data?.email
          });
        } else {
          console.warn('Profile load error:', error);
          // Set userProfile to empty object to indicate attempt was made
          setUserProfile({});
        }
      } catch (error) {
        console.error('Profile load error:', error);
        // Set userProfile to empty object to indicate attempt was made
        setUserProfile({});
      } finally {
        setProfileLoading(false);
      }
    },

    clear() {
      setUserProfile(null);
      setProfileLoading(false);
      localStorage?.removeItem('userRole');
      localStorage?.removeItem('userName');
    }
  };

  // Enhanced URL token processing for email verification
  const processUrlTokens = async () => {
    try {
      const hashParams = new URLSearchParams(window?.location?.hash?.substring(1));
      const accessToken = hashParams?.get('access_token');
      const refreshToken = hashParams?.get('refresh_token');
      const tokenType = hashParams?.get('token_type');
      const type = hashParams?.get('type');

      // Check if we have authentication tokens in the URL
      if (accessToken && refreshToken && type) {
        console.log('Processing auth tokens from URL:', { type, tokenType });

        if (type === 'signup' || type === 'email') {
          // Handle email verification tokens
          const { data, error } = await supabase?.auth?.verifyOtp({
            type: type === 'signup' ? 'signup' : 'email',
            token_hash: accessToken,
            options: {
              redirectTo: window?.location?.origin
            }
          });

          if (error) {
            console.error('Token verification error:', error);
            setAuthError('Email verification failed. The link may be expired or invalid.');
            // Clear URL parameters
            window?.history?.replaceState({}, document?.title, window?.location?.pathname);
            return false;
          } else if (data?.user) {
            console.log('Email verification successful');
            setAuthError(null);
            // Clear URL parameters
            window?.history?.replaceState({}, document?.title, window?.location?.pathname);
            return true;
          }
        }
      }
      return null;
    } catch (error) {
      console.error('Error processing URL tokens:', error);
      setAuthError('Authentication error. Please try logging in again.');
      return false;
    }
  };

  // ✅ REQUIRED: Protected auth handlers
  const authStateHandlers = {
    // CRITICAL: This MUST remain synchronous
    onChange: async (event, session) => {
      console.log('Auth state change:', { event, hasSession: !!session });
      setUser(session?.user ?? null);
      setLoading(false);

      if (session?.user) {
        // Check if user has confirmed email
        if (session?.user?.email_confirmed_at) {
          profileOperations?.load(session?.user?.id); // Fire-and-forget
          setAuthError(null); // Clear any auth errors on successful login
        } else {
          console.log('User email not yet confirmed, awaiting verification...');
          // IMPORTANT: Still set profileLoading to false for unconfirmed users
          setProfileLoading(false);
        }
      } else {
        profileOperations?.clear();
        // Don't clear auth errors when logging out, as user might need to see them
      }
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      // First, try to process any URL tokens
      const tokenResult = await processUrlTokens();
      
      // Get initial session
      const { data: { session } } = await supabase?.auth?.getSession();
      
      // Handle the session
      await authStateHandlers?.onChange(null, session);

      // If token processing was successful, redirect to appropriate page
      if (tokenResult === true && session?.user) {
        // Successful email verification, redirect to profile
        setTimeout(() => {
          if (window?.location?.pathname === '/register') {
            window.location.href = '/user-profile';
          }
        }, 1000);
      }
    };

    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    );

    return () => subscription?.unsubscribe();
  }, []);

  const signIn = async (email, password) => {
    try {
      setAuthError(null); // Clear previous errors
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password,
      });

      // Convert error to string to prevent React rendering issues
      return {
        data,
        error: error ? (error?.message || String(error)) : null
      };
    } catch (error) {
      console.error('SignIn catch error:', error);
      return {
        data: null,
        error: 'Network error. Please check your connection.'
      };
    }
  };

  const signUp = async (email, password, metadata = {}) => {
    try {
      setAuthError(null); // Clear previous errors
      
      // Enhanced metadata mapping for database trigger compatibility
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            // Map frontend camelCase to snake_case for database consistency
            full_name: metadata?.fullName || '',
            role: metadata?.role || 'client',
            postcode: metadata?.postcode || '',
            city: metadata?.city || '',
            email_updates: metadata?.emailUpdates || false,
            // Include all metadata fields
            ...metadata
          }
        }
      });

      // Enhanced error handling
      if (error) {
        console.error('SignUp error:', error);

        // Handle specific error types more gracefully
        let errorMessage = 'Registration failed. Please try again.';
        if (error?.message?.includes('email')) {
          errorMessage = 'This email address is already registered. Please try logging in instead.';
        } else if (error?.message?.includes('password')) {
          errorMessage = 'Password does not meet security requirements. Please use a stronger password.';
        } else if (error?.message?.includes('Users email has already been registered')) {
          errorMessage = 'An account with this email already exists. Please try signing in instead.';
        } else if (error?.message?.includes('Database error saving new user')) {
          errorMessage = 'Registration failed due to a server error. Please try again in a moment.';
        }

        return { data: null, error: errorMessage };
      }

      // If user creation successful and they have a session (auto-confirmed)
      if (data?.user && data?.session) {
        // User is immediately confirmed, load profile after short delay
        setTimeout(() => {
          profileOperations?.load(data?.user?.id);
        }, 500);
      } else if (data?.user) {
        // User created but needs email verification
        console.log('User created successfully, awaiting email verification...');
      }

      return { data, error: null };
    } catch (error) {
      console.error('SignUp catch error:', error);

      // Enhanced network error detection
      if (error?.message?.includes('Failed to fetch') ||
          error?.message?.includes('AuthRetryableFetchError') ||
          error?.message?.includes('Network request failed')) {
        return {
          data: null,
          error: 'Cannot connect to the server. Please check your internet connection and try again.'
        };
      }

      return { data: null, error: 'Registration failed. Please check your connection and try again.' };
    }
  };

  const signOut = async () => {
    const { error } = await supabase?.auth?.signOut();
    if (!error) {
      setUser(null);
      setAuthError(null); // Clear auth errors on logout
      profileOperations?.clear();
    }
    return { error };
  };

  // Clear auth error method
  const clearAuthError = () => {
    setAuthError(null);
  };

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    authError,
    signIn,
    signUp,
    signOut,
    clearAuthError,
    refreshProfile: () => user?.id && profileOperations?.load(user?.id)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};